import React from 'react'

import { FiltersSidebar } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { array, bool, func, number, oneOfType, string, object } from 'prop-types'

import WithTriangleUiContext from 'Pods/Shared/Hocs/WithTriangleUiContext'
import i18n from 'Pods/Shared/I18n'

import getSelectedFilters from 'Utils/getSelectedFilters'
import useBreakpoint from 'Utils/useBreakpoint'

import PriceSlider from '../../../Price/Components/PriceSlider'
import SubscriptionTier from '../../../SubscriptionFilter/Components/SubscriptionTier'
import {
  buildTierClearUpdate,
  buildTierFilterUpdate,
  getSelectedTier,
  isTierFilterAvailable,
  TIER_FILTER_KEY,
} from '../../../SubscriptionFilter/subscriptionTierFilters'

function RemainingPriceFilters({
  currentSelectedFilters,
  initialFrom = null,
  initialTo = null,
  maxPriceThreshold,
  minPriceThreshold,
  setMultipleFilters,
  setSelectedFilters,
  currentFilters,
  rangeFilters,
  selectedFilters,
  setShouldResetPriceRange,
  trackFilterChange,
  showSubscriptionTier = false,
  subscriberTier = null,
  locale = 'en',
}) {
  const windowSize = useBreakpoint()
  const shouldDisplayTitleDivider = windowSize === 'xs'

  const quickSelectionsLabel = i18n.t('listingPage.filters.quickSelections', { ns: 'rails', lng: locale })

  function removeFreeFilter() {
    const isFreeFilterActive = currentSelectedFilters.includes('free')
    if (isFreeFilterActive) {
      const newFiltersEntries = currentSelectedFilters
        .filter((value) => value && value !== 'free')
        .map((value) => [value, '1'])
      const newFilters = Object.fromEntries(newFiltersEntries)

      setMultipleFilters({ name: 'pricing', value: newFilters })
    }
  }

  function handleFilterChange(_, value, isOn) {
    const selectedPricingFilters = getSelectedFilters(selectedFilters.pricing || [])
    const selectedPriceRangeFilters = selectedFilters.priceRange || []

    if (value.includes('range_') || value.includes('more_than_')) {
      const newPriceRangeFilters = isOn
        ? [...selectedPriceRangeFilters, value]
        : selectedPriceRangeFilters.filter((item) => item !== value)
      removeFreeFilter()
      setSelectedFilters({ name: 'priceRange', value: newPriceRangeFilters })

      if (isOn) {
        trackFilterChange({ name: 'priceRange', value })
      } else {
        window.removeCurrentSearchFilter({ name: 'priceRange', value })
      }

      const rangeFiltersList = rangeFilters.map((item) => item.value)
      if (!rangeFiltersList.includes(value)) {
        setShouldResetPriceRange(true)
      }

      return
    }

    if (isOn && value === 'free') {
      setSelectedFilters({ name: 'priceRange', value: [] })
      setShouldResetPriceRange(true)
    }

    const filtersToRemove = (() => {
      if (value === 'free') {
        return ['exclude_free', 'subscribed']
      }
      if (value === 'exclude_free') {
        return ['free']
      }
      if (value === 'subscribed') {
        return ['free']
      }
      return []
    })()
    const newPricingFiltersEntries = (() => {
      if (isOn) {
        if (filtersToRemove.length > 0) {
          return [...selectedPricingFilters.filter((item) => !filtersToRemove.includes(item)), value]
        }

        return [...selectedPricingFilters, value]
      }

      return selectedPricingFilters.filter((item) => item !== value)
    })()
    const newPricingFilters = Object.fromEntries(newPricingFiltersEntries.map((item) => [item, '1']))
    setSelectedFilters({ name: 'pricing', value: newPricingFilters })

    if (isOn) {
      trackFilterChange({ name: 'pricing', value })
    } else {
      window.removeCurrentSearchFilter({ name: 'pricing', value })
    }
  }

  // Radio semantics: drop the previous tier so the tracked search state holds only the current one.
  function handleTierChange(tier) {
    window.removeCurrentSearchFilter({ name: 'subscriptionTier' })
    setSelectedFilters(buildTierFilterUpdate(tier, selectedFilters))
    trackFilterChange({ name: 'subscriptionTier', value: tier })
  }

  function handleTierClear() {
    window.removeCurrentSearchFilter({ name: 'subscriptionTier' })
    setSelectedFilters(buildTierClearUpdate(selectedFilters))
  }

  return (
    <div className="remaining-filters__price">
      <PriceSlider
        initialFrom={initialFrom}
        initialTo={initialTo}
        maxPriceThreshold={maxPriceThreshold}
        minPriceThreshold={minPriceThreshold}
        setSelectedFilters={setSelectedFilters}
        removeFreeFilter={removeFreeFilter}
        withTitleDivider={shouldDisplayTitleDivider}
      />
      <div className="remaining-filters__quick-selections">
        <FiltersSidebar
          isCollapsible={false}
          filters={[{ filters: rangeFilters, label: quickSelectionsLabel }]}
          selectedFilters={currentSelectedFilters}
          onFiltersChange={handleFilterChange}
          isControlled
          useNewCheckboxes
          dividerClassName="remaining-filters__divider"
          hideShowMore
        />
      </div>
      {showSubscriptionTier && (
        <SubscriptionTier
          selectedTier={getSelectedTier(
            selectedFilters[TIER_FILTER_KEY],
            getSelectedFilters(selectedFilters.pricing || [])
          )}
          subscriberTier={subscriberTier}
          onChange={handleTierChange}
          onClear={handleTierClear}
          name="remaining-subscription-tier"
          dividerClassName="remaining-filters__divider"
          locale={locale}
        />
      )}
      <FiltersSidebar
        isCollapsible={false}
        filters={[{ filters: currentFilters, label: null }]}
        selectedFilters={currentSelectedFilters}
        onFiltersChange={handleFilterChange}
        isControlled
        hideLastDivider
        useNewCheckboxes
        hideShowMore
      />
    </div>
  )
}

RemainingPriceFilters.propTypes = {
  currentSelectedFilters: array.isRequired,
  initialFrom: oneOfType([string, number]),
  initialTo: oneOfType([string, number]),
  maxPriceThreshold: number.isRequired,
  minPriceThreshold: number.isRequired,
  setMultipleFilters: func.isRequired,
  setSelectedFilters: func.isRequired,
  currentFilters: array.isRequired,
  rangeFilters: array.isRequired,
  selectedFilters: object.isRequired,
  setShouldResetPriceRange: func.isRequired,
  trackFilterChange: func.isRequired,
  showSubscriptionTier: bool,
  subscriberTier: string,
  locale: string,
}

const mapStateToProps = ({
  maxPriceThreshold,
  minPriceThreshold,
  selectedFilters,
  setMultipleFilters,
  setSelectedFilters,
  filtersOptions,
  setShouldResetPriceRange,
  trackFilterChange,
  locale,
  subscriptionFlowEnabled,
  premiumListingLabelEnabled,
  subscriptionV17CatalogEnabled,
  subscriberTier,
}) => {
  const freeOnlyLabel = i18n.t('listingPage.filters.price.freeOnly', { ns: 'rails', lng: locale })
  const onSaleLabel = i18n.t('listingPage.filters.price.onSale', { ns: 'rails', lng: locale, percentage: 70 })
  const showSubscriptionTier = isTierFilterAvailable({
    subscriptionFlowEnabled,
    premiumListingLabelEnabled,
    subscriptionV17CatalogEnabled,
  })

  const rangeFilters = filtersOptions.priceRange.filters || []
  const excludeFreeFilter = filtersOptions.pricing.filters.find(({ value }) => value === 'exclude_free')
  const pricingFilters = (filtersOptions.pricing.filters || [])
    .map((item) => {
      if (item.value === 'free') {
        return { ...item, label: freeOnlyLabel }
      }

      if (item.value === 'sale_off') {
        return { ...item, label: onSaleLabel }
      }

      if (item.value === 'subscribed') {
        return subscriptionFlowEnabled && !showSubscriptionTier ? item : null
      }

      return null
    })
    .sort((a, b) => b?.label.localeCompare(a?.label))
  const rangeFiltersList = rangeFilters.map(({ value }) => value)
  const selectedPricingFilters = selectedFilters.pricing || []
  const selectedPriceRangeFilters = selectedFilters.priceRange || []
  const customPriceRange = selectedPriceRangeFilters.find((value) => !rangeFiltersList.includes(value))
  const [initialFrom, initialTo] = (() => {
    if (customPriceRange) {
      const splitValue = customPriceRange.split('_').slice(1)
      return [splitValue[0], splitValue[1]]
    }

    return [undefined, undefined]
  })()

  const currentFilters = [...pricingFilters, excludeFreeFilter].filter(Boolean)
  const currentSelectedFilters = [...selectedPriceRangeFilters, ...getSelectedFilters(selectedPricingFilters)].filter(
    Boolean
  )

  return {
    currentFilters,
    currentSelectedFilters,
    initialFrom,
    initialTo,
    maxPriceThreshold,
    minPriceThreshold,
    setMultipleFilters,
    setSelectedFilters,
    rangeFilters,
    selectedFilters,
    setShouldResetPriceRange,
    trackFilterChange,
    locale,
    showSubscriptionTier,
    subscriberTier,
  }
}

export default WithTriangleUiContext(mapStateToProps)(RemainingPriceFilters)
